import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Button, TextField, Pagination, CircularProgress, Typography,
  Select, MenuItem, FormControl, InputLabel, Slider, Box, Chip, IconButton,
  Collapse, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 56px);
  background-color: #F8FCFF;
  padding: 24px;
  font-family: 'Sofia Sans', sans-serif;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #2C2C2D;
  font-family: 'Sofia Sans', sans-serif;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 16px;
`;

const StyledTableContainer = styled(TableContainer)`
  margin-bottom: 20px;
`;

const StyledTableHead = styled(TableHead)`
  background-color: #F0F0F0;
`;

const StyledTableCell = styled(TableCell)`
  font-weight: 600;
  text-align: center;
  font-family: 'Sofia Sans', sans-serif;
`;

const CenteredTableCell = styled(TableCell)`
  text-align: center;
  font-family: 'Sofia Sans', sans-serif;
`;

const StyledCheckIcon = styled(CheckIcon)`
  color: #169560;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;

const NoDataContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  flex-wrap: wrap;
  gap: 16px;
`;

const FilterSection = styled.div`
  background: #FFFFFF;
  border: 1px solid #E2E2E3;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
`;

const FilterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const FilterTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #2C2C2D;
  font-family: 'Sofia Sans', sans-serif;
  margin: 0;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 16px;
`;

const FilterControl = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterLabel = styled.label`
  font-family: 'Sofia Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #6D6D71;
`;

const RangeInputContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const RangeInput = styled(TextField)`
  & .MuiInputBase-root {
    font-family: 'Sofia Sans', sans-serif;
  }
`;

const ActiveFiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const SortableHeader = styled(StyledTableCell)`
  cursor: pointer;
  user-select: none;
  position: relative;
  
  &:hover {
    background-color: #E8E8E8;
  }
`;

const SortIcon = styled.span`
  margin-left: 4px;
  display: inline-flex;
  vertical-align: middle;
`;

const ViewToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CompletionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  font-family: 'Sofia Sans', sans-serif;
  background-color: ${props => {
    if (props.percentage >= 100) return '#E8F5E9';
    if (props.percentage >= 75) return '#FFF9C4';
    if (props.percentage >= 50) return '#FFE0B2';
    return '#FFCDD2';
  }};
  color: ${props => {
    if (props.percentage >= 100) return '#2E7D32';
    if (props.percentage >= 75) return '#F57F17';
    if (props.percentage >= 50) return '#E65100';
    return '#C62828';
  }};
`;

const AddButton = styled(Button)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 12px 16px;
  gap: 12px;
  width: 200px;
  height: 44px;
  background: #2575BD;
  border-radius: 16px;
  color: #FFFFFF;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 22px;
  text-transform: none;

  &:hover {
    background: #1c5c94;
  }
`;

const ButtonText = styled.span`
  flex: none;
  order: 1;
  flex-grow: 0;
`;

const ExportButton = styled(Button)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 12px 16px;
  gap: 12px;
  width: 200px;
  height: 44px;
  background: #169560;
  border-radius: 16px;
  color: #FFFFFF;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 22px;
  text-transform: none;

  &:hover {
    background: #117a4d;
  }

  &:disabled {
    background: #A7A7AA;
    color: #FFFFFF;
  }
`;

const StyledButton = styled(Button)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 12px 16px;
  gap: 12px;
  width: 157px;
  height: 44px;
  background: #2575BD;
  border-radius: 16px;
  color: #FFFFFF;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 22px;
  text-transform: none;

  &:hover {
    background: #1c5c94;
  }
`;

const ClickableParticipantId = styled.span`
  cursor: pointer;
  font-family: 'Sofia Sans', sans-serif;
  
  &:hover {
    color: #1c5c94;
  }
`;

const ListingPage = () => {
  const [participants, setParticipants] = useState([]);
  const [filteredParticipants, setFilteredParticipants] = useState([]);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [ageRange, setAgeRange] = useState([0, 100]);
  const [genderFilter, setGenderFilter] = useState('');
  const [valveCondition, setValveCondition] = useState('');
  const [completenessRange, setCompletenessRange] = useState([0, 100]);
  const [ejectionFractionRange, setEjectionFractionRange] = useState([0, 100]);

  // Sort states
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');

  // View toggle
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchAllParticipants();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [participants, ageRange, genderFilter, valveCondition, completenessRange, ejectionFractionRange, sortField, sortDirection]);

  useEffect(() => {
    updatePagination();
  }, [filteredParticipants, itemsPerPage]);

  const fetchAllParticipants = async () => {
    setLoading(true);
    try {
      // Fetch all participants in a single optimized API call
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}participant/all-for-listing`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setParticipants(response.data.participants);
      setTotalCount(response.data.total_count);
    } catch (error) {
      console.error('Error fetching participants:', error);
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateCompleteness = (participant) => {
    const flags = participant.flags;
    const totalSections = 7; // info, clinical, general, cardiac, ecg, echocardiogram, ecg_file

    let completed = 0;
    if (flags.info) completed++;
    if (flags.clinical) completed++;
    if (flags.general) completed++;
    if (flags.cardiac) completed++;
    if (flags.ecg) completed++;
    if (flags.echocardiogram) completed++;
    if (flags.ecg_file) completed++;

    return Math.round((completed / totalSections) * 100);
  };

  const getValveCondition = (participant) => {
    const echo = participant.echocardiogram;
    if (!echo) return 'Unknown';

    const valves = ['mitral_valve', 'tricuspid_valve', 'aortic_valve', 'pulmonary_valve'];
    const conditions = [];

    valves.forEach(valve => {
      const valveData = echo[valve];
      if (valveData) {
        if (valveData.regurgitation_grade && valveData.regurgitation_grade !== 'None') {
          conditions.push(`${valve.split('_')[0]} regurgitation`);
        }
        if (valveData.stenosis_grade && valveData.stenosis_grade !== 'None') {
          conditions.push(`${valve.split('_')[0]} stenosis`);
        }
      }
    });

    return conditions.length > 0 ? conditions.join(', ') : 'Normal';
  };

  const applyFiltersAndSort = () => {
    let filtered = [...participants];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.participant_id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply age filter
    filtered = filtered.filter(p => {
      const age = parseInt(p.demographics?.age);
      // Include participants without age data if range starts at 0
      if (isNaN(age)) return ageRange[0] === 0;
      return age >= ageRange[0] && age <= ageRange[1];
    });

    // Apply gender filter
    if (genderFilter) {
      filtered = filtered.filter(p =>
        p.demographics?.sex?.toLowerCase() === genderFilter.toLowerCase()
      );
    }

    // Apply valve condition filter
    if (valveCondition) {
      filtered = filtered.filter(p => {
        const condition = getValveCondition(p);
        if (valveCondition === 'Normal') return condition === 'Normal';
        if (valveCondition === 'Abnormal') return condition !== 'Normal' && condition !== 'Unknown';
        return true;
      });
    }

    // Apply completeness filter
    filtered = filtered.filter(p => {
      const completeness = calculateCompleteness(p);
      return completeness >= completenessRange[0] && completeness <= completenessRange[1];
    });

    // Apply ejection fraction filter
    filtered = filtered.filter(p => {
      const ef = parseFloat(p.echocardiogram?.ventricular_measurements?.ejection_fraction);
      if (isNaN(ef)) return ejectionFractionRange[0] === 0; // Include unknown if range starts at 0
      return ef >= ejectionFractionRange[0] && ef <= ejectionFractionRange[1];
    });

    // Apply sorting
    if (sortField) {
      filtered.sort((a, b) => {
        let aValue, bValue;

        switch (sortField) {
          case 'participant_id':
            aValue = a.participant_id;
            bValue = b.participant_id;
            break;
          case 'last_modified':
            aValue = a.last_modified || 0;
            bValue = b.last_modified || 0;
            break;
          case 'completeness':
            aValue = calculateCompleteness(a);
            bValue = calculateCompleteness(b);
            break;
          case 'age':
            aValue = parseInt(a.demographics?.age) || 0;
            bValue = parseInt(b.demographics?.age) || 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredParticipants(filtered);
  };

  const updatePagination = () => {
    if (itemsPerPage === -1) {
      setTotalPages(1);
    } else {
      setTotalPages(Math.ceil(filteredParticipants.length / itemsPerPage));
    }
    setPage(1); // Reset to first page when filters change
  };

  const getCurrentPageData = () => {
    if (itemsPerPage === -1) {
      return filteredParticipants;
    }
    const startIndex = (page - 1) * itemsPerPage;
    return filteredParticipants.slice(startIndex, startIndex + itemsPerPage);
  };

  const handleSearch = () => {
    applyFiltersAndSort();
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleClearFilters = () => {
    setAgeRange([0, 100]);
    setGenderFilter('');
    setValveCondition('');
    setCompletenessRange([0, 100]);
    setEjectionFractionRange([0, 100]);
    setSearchTerm('');
    setSortField('');
    setSortDirection('asc');
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (ageRange[0] !== 0 || ageRange[1] !== 100) count++;
    if (genderFilter) count++;
    if (valveCondition) count++;
    if (completenessRange[0] !== 0 || completenessRange[1] !== 100) count++;
    if (ejectionFractionRange[0] !== 0 || ejectionFractionRange[1] !== 100) count++;
    return count;
  };

  const allFilesUploaded = (participant) => {
    return participant.flags.ecg_file &&
      participant.flags.echocardiogram_file;
  };

  const handleEditClick = (participantId, incompleteTab) => {
    navigate(`/form/${participantId}`, { state: { activeTab: incompleteTab, participantId } });
  };

  const getIncompleteTab = (flags) => {
    const tabOrder = ['info', 'clinical', 'general', 'cardiac', 'ecg', 'echocardiogram', 'fileUpload'];
    return tabOrder.find(tab => !flags[tab]) || 'participantInfo';
  };

  const handleAddNewRecord = () => {
    navigate('/landing');
  };

  const handleParticipantIdClick = (participantId) => {
    navigate(`/form/${participantId}`, { state: { activeTab: 'participantInfo', participantId } });
  };

  const handleExportXLSX = async () => {
    setExporting(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}participant/export-xlsx`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob',
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `CRF_Export_${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting XLSX:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return (
      <SortIcon>
        {sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
      </SortIcon>
    );
  };

  const currentPageData = getCurrentPageData();

  return (
    <PageContainer>
      <Header>
        <TitleContainer>
          <Title>Data Collection - Listing Page</Title>
          <AddButton onClick={handleAddNewRecord}>
            <AddIcon />
            <ButtonText>Add New Record</ButtonText>
          </AddButton>
          <ExportButton onClick={handleExportXLSX} disabled={exporting || loading}>
            <FileDownloadIcon />
            <ButtonText>{exporting ? 'Exporting...' : 'Export XLSX'}</ButtonText>
          </ExportButton>
        </TitleContainer>
        <SearchContainer>
          <TextField
            placeholder="Search participant"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              style: { fontFamily: 'Sofia Sans, sans-serif' }
            }}
          />
          <StyledButton onClick={handleSearch}>
            <SearchIcon />
            <ButtonText>Search</ButtonText>
          </StyledButton>
          <IconButton
            onClick={() => setShowFilters(!showFilters)}
            style={{
              backgroundColor: showFilters ? '#2575BD' : '#F0F0F0',
              color: showFilters ? '#FFFFFF' : '#2C2C2D'
            }}
          >
            <FilterListIcon />
            {getActiveFilterCount() > 0 && (
              <span style={{
                marginLeft: '4px',
                fontSize: '12px',
                fontWeight: 'bold',
                fontFamily: 'Sofia Sans, sans-serif'
              }}>
                ({getActiveFilterCount()})
              </span>
            )}
          </IconButton>
        </SearchContainer>
      </Header>

      {showFilters && (
        <FilterSection>
          <FilterHeader>
            <FilterTitle>Filters</FilterTitle>
            <Button
              size="small"
              onClick={handleClearFilters}
              style={{ fontFamily: 'Sofia Sans, sans-serif' }}
            >
              Clear All
            </Button>
          </FilterHeader>

          <FilterGrid>
            <FilterControl>
              <FilterLabel>Age Range</FilterLabel>
              <Box sx={{ px: 2 }}>
                <Slider
                  value={ageRange}
                  onChange={(e, newValue) => setAgeRange(newValue)}
                  valueLabelDisplay="auto"
                  min={0}
                  max={100}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 50, label: '50' },
                    { value: 100, label: '100' }
                  ]}
                />
              </Box>
              <Typography variant="caption" style={{ fontFamily: 'Sofia Sans, sans-serif', textAlign: 'center' }}>
                {ageRange[0]} - {ageRange[1]} years
              </Typography>
            </FilterControl>

            <FilterControl>
              <FilterLabel>Gender</FilterLabel>
              <FormControl fullWidth size="small">
                <Select
                  value={genderFilter}
                  onChange={(e) => setGenderFilter(e.target.value)}
                  displayEmpty
                  style={{ fontFamily: 'Sofia Sans, sans-serif' }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                </Select>
              </FormControl>
            </FilterControl>

            <FilterControl>
              <FilterLabel>Valve Condition</FilterLabel>
              <FormControl fullWidth size="small">
                <Select
                  value={valveCondition}
                  onChange={(e) => setValveCondition(e.target.value)}
                  displayEmpty
                  style={{ fontFamily: 'Sofia Sans, sans-serif' }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Normal">Normal</MenuItem>
                  <MenuItem value="Abnormal">Abnormal</MenuItem>
                </Select>
              </FormControl>
            </FilterControl>

            <FilterControl>
              <FilterLabel>Data Completeness (%)</FilterLabel>
              <Box sx={{ px: 2 }}>
                <Slider
                  value={completenessRange}
                  onChange={(e, newValue) => setCompletenessRange(newValue)}
                  valueLabelDisplay="auto"
                  min={0}
                  max={100}
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 50, label: '50%' },
                    { value: 100, label: '100%' }
                  ]}
                />
              </Box>
              <Typography variant="caption" style={{ fontFamily: 'Sofia Sans, sans-serif', textAlign: 'center' }}>
                {completenessRange[0]}% - {completenessRange[1]}%
              </Typography>
            </FilterControl>

            <FilterControl>
              <FilterLabel>Ejection Fraction (%)</FilterLabel>
              <Box sx={{ px: 2 }}>
                <Slider
                  value={ejectionFractionRange}
                  onChange={(e, newValue) => setEjectionFractionRange(newValue)}
                  valueLabelDisplay="auto"
                  min={0}
                  max={100}
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 50, label: '50%' },
                    { value: 100, label: '100%' }
                  ]}
                />
              </Box>
              <Typography variant="caption" style={{ fontFamily: 'Sofia Sans, sans-serif', textAlign: 'center' }}>
                {ejectionFractionRange[0]}% - {ejectionFractionRange[1]}%
              </Typography>
            </FilterControl>
          </FilterGrid>

          {getActiveFilterCount() > 0 && (
            <ActiveFiltersContainer>
              <Typography variant="body2" style={{ fontFamily: 'Sofia Sans, sans-serif', marginRight: '8px' }}>
                Active Filters:
              </Typography>
              {(ageRange[0] !== 0 || ageRange[1] !== 100) && (
                <Chip
                  label={`Age: ${ageRange[0]}-${ageRange[1]}`}
                  size="small"
                  onDelete={() => setAgeRange([0, 100])}
                />
              )}
              {genderFilter && (
                <Chip
                  label={`Gender: ${genderFilter}`}
                  size="small"
                  onDelete={() => setGenderFilter('')}
                />
              )}
              {valveCondition && (
                <Chip
                  label={`Valve: ${valveCondition}`}
                  size="small"
                  onDelete={() => setValveCondition('')}
                />
              )}
              {(completenessRange[0] !== 0 || completenessRange[1] !== 100) && (
                <Chip
                  label={`Completeness: ${completenessRange[0]}-${completenessRange[1]}%`}
                  size="small"
                  onDelete={() => setCompletenessRange([0, 100])}
                />
              )}
              {(ejectionFractionRange[0] !== 0 || ejectionFractionRange[1] !== 100) && (
                <Chip
                  label={`EF: ${ejectionFractionRange[0]}-${ejectionFractionRange[1]}%`}
                  size="small"
                  onDelete={() => setEjectionFractionRange([0, 100])}
                />
              )}
            </ActiveFiltersContainer>
          )}
        </FilterSection>
      )}

      <StyledTableContainer component={Paper}>
        {loading ? (
          <LoadingContainer>
            <CircularProgress />
          </LoadingContainer>
        ) : currentPageData.length === 0 ? (
          <NoDataContainer>
            <Typography style={{ fontFamily: 'Sofia Sans, sans-serif' }}>No participants found</Typography>
          </NoDataContainer>
        ) : (
          <Table>
            <StyledTableHead>
              <TableRow>
                <SortableHeader onClick={() => handleSort('participant_id')}>
                  Patient ID {renderSortIcon('participant_id')}
                </SortableHeader>
                <SortableHeader onClick={() => handleSort('last_modified')}>
                  Last Modified {renderSortIcon('last_modified')}
                </SortableHeader>
                <SortableHeader onClick={() => handleSort('age')}>
                  Age {renderSortIcon('age')}
                </SortableHeader>
                <StyledTableCell>Gender</StyledTableCell>
                <SortableHeader onClick={() => handleSort('completeness')}>
                  Completeness {renderSortIcon('completeness')}
                </SortableHeader>
                <StyledTableCell>Participant Info</StyledTableCell>
                <StyledTableCell>Clinical</StyledTableCell>
                <StyledTableCell>General Checkup</StyledTableCell>
                <StyledTableCell>Cardiac Auscultation</StyledTableCell>
                <StyledTableCell>ECG</StyledTableCell>
                <StyledTableCell>Echocardiogram</StyledTableCell>
                <StyledTableCell>File Upload</StyledTableCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {currentPageData.map((participant) => {
                const incompleteTab = getIncompleteTab(participant.flags);
                const completeness = calculateCompleteness(participant);
                return (
                  <TableRow key={participant.participant_id}>
                    <CenteredTableCell>
                      <ClickableParticipantId onClick={() => handleParticipantIdClick(participant.participant_id)}>
                        {participant.participant_id}
                      </ClickableParticipantId>
                    </CenteredTableCell>
                    <CenteredTableCell>
                      {participant.last_modified ? new Date(participant.last_modified * 1000).toLocaleDateString() : 'N/A'}
                    </CenteredTableCell>
                    <CenteredTableCell>
                      {participant.demographics?.age || 'N/A'}
                    </CenteredTableCell>
                    <CenteredTableCell>
                      {participant.demographics?.sex ? participant.demographics.sex.charAt(0).toUpperCase() + participant.demographics.sex.slice(1) : 'N/A'}
                    </CenteredTableCell>
                    <CenteredTableCell>
                      <CompletionBadge percentage={completeness}>
                        {completeness}%
                      </CompletionBadge>
                    </CenteredTableCell>
                    <CenteredTableCell>
                      {participant.flags.info ? <StyledCheckIcon /> : <EditIcon onClick={() => handleEditClick(participant.participant_id, 'participantInfo')} style={{ cursor: 'pointer' }} />}
                    </CenteredTableCell>
                    <CenteredTableCell>
                      {participant.flags.clinical ? <StyledCheckIcon /> : <EditIcon onClick={() => handleEditClick(participant.participant_id, 'clinicalMeasurements')} style={{ cursor: 'pointer' }} />}
                    </CenteredTableCell>
                    <CenteredTableCell>
                      {participant.flags.general ? <StyledCheckIcon /> : <EditIcon onClick={() => handleEditClick(participant.participant_id, 'generalCheckup')} style={{ cursor: 'pointer' }} />}
                    </CenteredTableCell>
                    <CenteredTableCell>
                      {participant.flags.cardiac ? <StyledCheckIcon /> : <EditIcon onClick={() => handleEditClick(participant.participant_id, 'cardiacAuscultation')} style={{ cursor: 'pointer' }} />}
                    </CenteredTableCell>
                    <CenteredTableCell>
                      {participant.flags.ecg ? <StyledCheckIcon /> : <EditIcon onClick={() => handleEditClick(participant.participant_id, 'ecgForm')} style={{ cursor: 'pointer' }} />}
                    </CenteredTableCell>
                    <CenteredTableCell>
                      {participant.flags.echocardiogram ? <StyledCheckIcon /> : <EditIcon onClick={() => handleEditClick(participant.participant_id, 'echocardiogram')} style={{ cursor: 'pointer' }} />}
                    </CenteredTableCell>
                    <CenteredTableCell>
                      {allFilesUploaded(participant) ? <StyledCheckIcon /> : <EditIcon onClick={() => handleEditClick(participant.participant_id, 'fileUpload')} style={{ cursor: 'pointer' }} />}
                    </CenteredTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </StyledTableContainer>

      <PaginationContainer>
        <ViewToggleContainer>
          <Typography variant="body2" style={{ fontFamily: 'Sofia Sans, sans-serif' }}>
            Show:
          </Typography>
          <FormControl size="small">
            <Select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(e.target.value)}
              style={{ fontFamily: 'Sofia Sans, sans-serif' }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={-1}>All</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" style={{ fontFamily: 'Sofia Sans, sans-serif' }}>
            entries per page
          </Typography>
        </ViewToggleContainer>

        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <Typography style={{ fontFamily: 'Sofia Sans, sans-serif' }}>
            Showing {currentPageData.length > 0 ? ((page - 1) * (itemsPerPage === -1 ? filteredParticipants.length : itemsPerPage)) + 1 : 0} - {Math.min(page * (itemsPerPage === -1 ? filteredParticipants.length : itemsPerPage), filteredParticipants.length)} of {filteredParticipants.length} participants
          </Typography>
          {itemsPerPage !== -1 && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              disabled={loading}
            />
          )}
        </div>
      </PaginationContainer>
    </PageContainer>
  );
};

export default ListingPage;
